import bindAllOfThis from "src/utils/BindThisHelper";
import { PhaserGamePad } from "src/games/shared/Gamepad";
import { PhaserModalConfig, PhaserModal, ModalOption } from 'src/games/shared/Modal';
import TranslateApi from 'src/utils/TranslateHelper';
import { TiledGameObject } from '../../shared/phaserHelpers/HandyTypes';
import { Student } from '../objects/Student';
import { College } from 'src/games/UniversityGame/objects/College';
import { Color } from "src/games/shared/ColorValue";
import { EndGameScene } from "src/games/UniversityGame/scenes/EndGameScene";
import AudioTranslations from "src/constants/AudioTranslations";

export class MainScene extends Phaser.Scene {
    gameWidth: number;
    gameHeight: number;
    player: Phaser.Physics.Arcade.Sprite;
    endGame: boolean;
    stopAllMovement: boolean;
    distanceBetween: number = 32;
    speed: number = 200;
    generalSize: number = 32;
    gamePad: PhaserGamePad;
    groundLayer: Phaser.Tilemaps.DynamicTilemapLayer;
    collegeLayer: Phaser.Tilemaps.DynamicTilemapLayer;
    tiles: Phaser.Tilemaps.Tileset;
    map: Phaser.Tilemaps.Tilemap;
    popupMap: Phaser.GameObjects.Image;
    popupText: Phaser.GameObjects.Text[];
    startPosition: Phaser.Geom.Rectangle;
    students: Student[];
    currentStudent: Student;
    colleges: College[];
    astronaut: Phaser.GameObjects.Image;
    speechBubble: Phaser.GameObjects.Container;
    collisionLayer: Phaser.Tilemaps.ObjectLayer;
    gatesLayer: Phaser.Tilemaps.ObjectLayer;
    gateCollisions: Phaser.Physics.Arcade.Collider[];
    gateIcons: Phaser.GameObjects.Image[];
    keysLayer: Phaser.Tilemaps.ObjectLayer;
    keyIcons: Phaser.GameObjects.Image[];
    gateTimeout: Phaser.Time.TimerEvent;
    gateText: Phaser.GameObjects.Text;
    gateTextBorder: Phaser.GameObjects.Image;
    timerStart: boolean;
    npcUp: boolean;
    npcDown: boolean;
    npcLeft: boolean;
    npcRight: boolean;
    playerHasKey: boolean = false;

    xVel: number = 0;
    yVel: number = 0;

    private translateString = "University.Phaser.";

    constructor() {
        super({
            key: "MainScene"
        });
    }

    preload(): void {
        this.load.image("pixel", require("/src/assets/qci/sprites/pixel.png"));
        this.load.tilemapTiledJSON("map", require("/src/assets/qci/maps/university_map/campus.json"));
        this.load.spritesheet('player', require('src/assets/qci/sprites/studentPilot/student_spritemap.png'), { frameWidth: 32, frameHeight: 48 });
        this.load.image('tiles', require('/src/assets/qci/atlas/builder/gameUniversity/sheet.png'));
        this.load.atlas('aliens', require('/src/assets/qci/atlas/smallAliens.png'), require('/src/assets/qci/atlas/smallAliens.json'));
        this.load.image('help', require("/src/assets/qci/sprites/chat-help.png"));
        this.load.image('campusMap', require('/src/assets/qci/atlas/builder/gameUniversity/campusWholeMap_colleges.png'));
        this.load.image('gate', require("/src/assets/online-assets/sprites/gate.png"));
        this.load.image('key', require("/src/assets/online-assets/sprites/key.png"));
        this.load.atlas('pilots', require("/src/assets/qci/atlas/starPilotAll.png"), require("/src/assets/qci/atlas/starPilotAll.json"));
        PhaserGamePad.preload(this);

        var keys = [
            "University.Phaser.NPCs.artsStudent.goodbye",
            "University.Phaser.NPCs.artsStudent.greeting",
            "University.Phaser.NPCs.businessStudent.goodbye",
            "University.Phaser.NPCs.businessStudent.greeting",
            "University.Phaser.NPCs.engineeringStudent.goodbye",
            "University.Phaser.NPCs.engineeringStudent.greeting",
            "University.Phaser.NPCs.medicalStudent.goodbye",
            "University.Phaser.NPCs.medicalStudent.greeting",
        ];

        keys.forEach((key) => {
            this.load.audio(key, AudioTranslations.FindByKeyAndLanguage(key, TranslateApi.currentLanguage.code).url);
        });
    }

    initializeGame(): void {
        this.currentStudent = null;
        this.endGame = false;
        this.students.forEach((s: Student) => {
            s.complete = false;
        });
        this.playerHasKey = false;
    }

    create(): void {
        this.gameWidth = this.textures.game.config.width as number;
        this.gameHeight = this.textures.game.config.height as number;
        this.stopAllMovement = false;
        bindAllOfThis(this, MainScene.prototype);
        this.initializeMap();
        this.addNPCs();
        this.addNPStudents();
        this.createPopUpMap();
        this.generateGamepad();
        this.createColliders();
        this.setupCameras();
        this.setWalkCollisions();
        this.addObstacles();
        this.addKeys();
        this.currentStudent = null;
        this.initializeGame();

        this.speechBubble = this.createBubble(0, 100, TranslateApi.translate(this.translateString + "wrongCollege"));
        this.speechBubble.setX(this.gameWidth / 2 - this.speechBubble.width / 2);
        this.speechBubble.setVisible(false);

        this.children.bringToTop(this.player);
    }

    initializeMap() {
        this.map = this.make.tilemap({ key: "map" });
        this.tiles = this.map.addTilesetImage('tiles');

        this.keysLayer = this.map.getObjectLayer('Keys');
        this.gatesLayer = this.map.getObjectLayer('Gates');
        this.gateTimeout = null;
        this.gateCollisions = [];
        this.gateIcons = [];
        this.keyIcons = [];
        this.collisionLayer = this.map.getObjectLayer('Collisions');
        var backgroundLayer = this.map.createDynamicLayer('Background', this.tiles, 0, 0);
        this.groundLayer = this.map.createDynamicLayer('Ground Layer', this.tiles, 0, 0);
        this.collegeLayer = this.map.createDynamicLayer('Colleges', this.tiles, 0, 0);

        // Boundaries
        this.groundLayer.setCollisionBetween(246, 248);
    }

    createPopUpMap(): void {
        var config = { fontFamily: "Russo One, Roboto Mono, Arial", fill: "#ffffff", align: "center", fontSize: 36 };
        this.popupText = [];
        this.popupText.push(this.add.text(140, 90, TranslateApi.translate(this.translateString + "Map.1"), config));
        this.popupText.push(this.add.text(1025, 75, TranslateApi.translate(this.translateString + "Map.2"), config));
        this.popupText.push(this.add.text(90, 420, TranslateApi.translate(this.translateString + "Map.3"), config));
        this.popupText.push(this.add.text(960, 445, TranslateApi.translate(this.translateString + "Map.4"), config));
        var text: Phaser.GameObjects.Text;
        for (text of this.popupText) {
            text.setOrigin(0);
            text.setScrollFactor(0);
            text.setDepth(91);
            text.setWordWrapWidth(200);
            text.setVisible(false);
        }

        this.popupMap = this.add.image(0, 0, "campusMap");
        this.popupMap.setOrigin(0);
        this.popupMap.setDisplaySize(this.gameWidth, this.gameHeight);
        this.popupMap.setScrollFactor(0);
        this.popupMap.setDepth(90);
        this.popupMap.setVisible(false);
    }

    addObstacles(): void {
        var count = 0;
        this.gatesLayer.objects.forEach((object: TiledGameObject) => {
            // The group will have an array of objects - these are the individual collision shapes
            var gameObject = new Phaser.GameObjects.Zone(this, object.x, object.y, object.width, object.height);
            gameObject.type = object.type;
            this.physics.add.existing(gameObject, true);
            this.gateCollisions[count] = this.physics.add.collider(this.player, gameObject);
            if (count === 0) {
                this.gateIcons[count] = this.add.image(gameObject.getCenter().x, gameObject.getCenter().y, 'gate');
            }
            else if (count === 1) {
                this.gateIcons[count] = this.add.image(gameObject.getCenter().x + gameObject.width, gameObject.getCenter().y, 'gate').setAngle(90);
            }
            this.gateIcons[count].setDepth(15).setDisplaySize(gameObject.width, gameObject.height).setOrigin(0);
            count++;
        });
        this.makeGateText();
    }

    addKeys() {
        var count = 0;
        this.keysLayer.objects.forEach((object: TiledGameObject) => {
            // The group will have an array of objects - these are the individual collision shapes
            var gameObject = new Phaser.GameObjects.Zone(this, object.x, object.y, object.width, object.height);
            gameObject.type = object.type;
            if (count === 0) {
                this.keyIcons[count] = this.add.image(gameObject.getCenter().x + gameObject.width, gameObject.getCenter().y, 'key');
            }
            else if (count === 1) {
                this.keyIcons[count] = this.add.image(gameObject.getCenter().x + gameObject.width, gameObject.getCenter().y, 'key');
            }
            this.keyIcons[count].setDepth(15).setDisplaySize(object.width, object.height).setOrigin(0);
            count++;
        });
    }

    setWalkCollisions(): void {
        this.collisionLayer.objects.forEach((object: TiledGameObject) => {
            // The group will have an array of objects - these are the individual collision shapes
            var gameObject = new Phaser.GameObjects.Zone(this, object.x, object.y, object.width, object.height);

            gameObject.type = object.type;
            this.physics.add.existing(gameObject, true);
            this.physics.add.collider(this.player, gameObject);
        });
    }

    createColliders() {
        this.physics.add.collider(this.player, this.groundLayer);
        this.students.forEach((s: Student) => {
            this.physics.add.collider(s.object, this.groundLayer);
        });

        // Set collisions by tiled's tileset editor collider bounds
        this.groundLayer.forEachTile((tile) => {
            var tileWorldPos = this.groundLayer.tileToWorldXY(tile.x, tile.y);
            var collisionGroup = this.tiles.getTileCollisionGroup(tile.index);
            if (!collisionGroup || collisionGroup["objects"].length === 0) { return; }
            // The group will have an array of objects - these are the individual collision shapes
            var objects = collisionGroup["objects"];
            for (var i = 0; i < objects.length; i++) {
                var object = objects[i];
                var objectX = tileWorldPos.x + object.x;
                var objectY = tileWorldPos.y + object.y;
                // Add collider for rectangles
                if (object.rectangle) {
                    var gameObject = new Phaser.GameObjects.Zone(this, objectX, objectY, object.width, object.height);
                    this.physics.add.existing(gameObject, true);
                    this.collideAll(gameObject);
                }
            }
        });

        // Colleges
        this.collegeLayer.forEachTile((tile) => {
            var tileWorldPos = this.collegeLayer.tileToWorldXY(tile.x, tile.y);
            var collisionGroup = this.tiles.getTileCollisionGroup(tile.index);
            if (!collisionGroup || collisionGroup["objects"].length === 0) { return; }
            // The group will have an array of objects - these are the individual collision shapes
            var objects = collisionGroup["objects"];
            for (var i = 0; i < objects.length; i++) {
                var object = objects[i];
                var objectX = tileWorldPos.x + object.x;
                var objectY = tileWorldPos.y + object.y;
                // Add collider for rectangles
                if (object.rectangle) {
                    var gameObject = new Phaser.GameObjects.Zone(this, objectX, objectY, object.width, object.height);
                    this.physics.add.existing(gameObject, true);
                    this.collideAll(gameObject);
                }
            }
        });
    }

    createBubble(x: number, y: number, text: string): Phaser.GameObjects.Container {
        var padding = 10;
        var fontSize = 20;
        var borderWidth = 2;
        var borderColor = Color.PLANET_COLORS.get("I");
        var textBox = this.add.container(x, y);
        var textStyle = { fontFamily: 'Russo One, Arial', color: "#ffffff", fontSize: fontSize, padding: 10 };

        var textObject = this.add.text(padding + borderWidth, padding + borderWidth, text, textStyle);
        textObject.setWordWrapWidth(400 + 2 * padding, true);
        textObject.setName("Bubble");
        textBox.setSize(textObject.displayWidth + 2 * padding + borderWidth, textObject.displayHeight + 2 * padding + borderWidth);

        // Add text box background
        var textBoxBackground = this.add.image(0, 0, "pixel");
        textBoxBackground.setOrigin(0);
        textBoxBackground.setDisplaySize(textBox.displayWidth, textBox.displayHeight);
        textBoxBackground.setTint(0x000000);
        textBoxBackground.setPosition(borderWidth + padding + textStyle.padding, borderWidth + padding + textStyle.padding);

        // Add text box border
        var textBoxBorder = this.add.image(0, 0, "pixel");
        textBoxBorder.setOrigin(0);
        textBoxBorder.setDisplaySize(textBoxBackground.displayWidth + 2 * borderWidth, textBoxBackground.displayHeight + 2 * borderWidth);
        textBoxBorder.setTint(borderColor);
        textBoxBackground.setPosition(borderWidth, borderWidth);

        textBox.add(textBoxBorder);
        textBox.add(textBoxBackground);
        textBox.add(textObject);

        textBox.setScrollFactor(0);

        return textBox;
    }

    collideAll(object): void {
        this.physics.add.collider(this.player, object);
        this.students.forEach((s: Student) => {
            this.physics.add.collider(s.object, object);
        });
    }

    addNPCs(): void {
        var layer = this.map.getObjectLayer("NPCS").objects;
        var areas: TiledGameObject[] = [];
        layer.forEach((ob: TiledGameObject) => {
            if (ob.name === "player") {
                this.generatePlayer(ob);
            }
            else if (ob.name === "1" || ob.name === '2' || ob.name === '3' || ob.name === '4') {
                areas.push(ob);
            }
        });
        this.createStudentNPCs(areas);
    }

    addNPStudents(): void {
        var layer = this.map.getObjectLayer("NPCS").objects;
        layer.forEach((ob: TiledGameObject) => {
            if (ob.name === "100") {
                var num = Math.floor(Math.random() * 4);
                var alien = "snib-1";
                if (num === 0) { "snib-1"; }
                else if (num === 1) { alien = "snib-2"; }
                else if (num === 2) { alien = "sleb-1"; }
                else if (num === 3) { alien = "sleb-2"; }
                var al = this.physics.add.sprite(ob.x, ob.y, "aliens", alien);
                al.setDisplaySize(32, 32);
                al.setOrigin(0);
                al.setOffset(32, 32);
            }
        });
    }

    modalPop(text: string, buttonKey: string) {
        this.player.setVelocity(0, 0);
        this.stopAllMovement = true;
        this.player.disableBody();
        var config = new PhaserModalConfig();
        config.title = text;
        config.buttonConfig.smartSize = true;
        config.forceResponse = true;
        var buttonText;
        if(buttonKey === "done") {
            buttonText = TranslateApi.translate("common.done");
        }
        else if(buttonKey === "yes") {
            buttonText = TranslateApi.translate("admin.common.confirmYes");
        }
        else if(buttonKey === "ok") {
            buttonText = TranslateApi.translate("common.ok");
        }

        var modal = new PhaserModal(this, 0, 0, [{ id: 1, displayText: buttonText }], config);
        modal.setPosition(this.gameWidth / 2 - modal.width / 2, this.gameHeight / 2 - modal.height / 2).setDepth(20);
        modal.on('optionClicked', () => this.modalOptionClicked(modal));
    }

    modalOptionClicked(modal: PhaserModal) {
        modal.destroy();
        this.player.enableBody(false, this.player.x, this.player.y, true, true);
        if (this.endGame) {
            this.player.disableBody();
            this.input.removeAllListeners();
            this.input.setDefaultCursor("default");
            this.scene.add("EndGameScene", EndGameScene, true);
            this.scene.remove(this);
        }
        this.stopAllMovement = false;
    }

    setupCameras() {
        this.cameras.main.setBounds(0, 0, this.map.widthInPixels, this.map.heightInPixels);
        this.cameras.main.startFollow(this.player, true);
    }

    /**
    * Generates the player with animations
    * */
    generatePlayer(ob: TiledGameObject) {
        this.player = this.physics.add.sprite(ob.x, ob.y, "player");
        this.startPosition = new Phaser.Geom.Rectangle(ob.x, ob.y, ob.width, ob.height);
        this.player.setOrigin(0);

        // For collision bounds
        this.player.setSize(20, 38);
        this.player.setOffset(22, 34);
        this.player.setDisplaySize(32, 48);
        this.player.setFrame(4);
        this.player.setDepth(15);

        //  Player animations, turning, walking left and walking right.
        this.anims.create({
            key: 'left',
            frames: this.anims.generateFrameNumbers('player', { start: 0, end: 3 }),
            frameRate: 10,
            repeat: -1
        });

        this.anims.create({
            key: 'turn',
            frames: [{ key: 'player', frame: 4 }],
            frameRate: 20
        });

        this.anims.create({
            key: 'right',
            frames: this.anims.generateFrameNumbers('player', { start: 5, end: 8 }),
            frameRate: 10,
            repeat: -1
        });
    }

    createStudentNPCs(obs: TiledGameObject[]) {
        this.students = [];
        this.colleges = [];
        var students = [...Student.STUDENTS];
        students.forEach((s: Student) => {
            var ob = obs.pop();
            var object = this.physics.add.sprite(ob.x, ob.y, "aliens", s.frame);
            object.setOrigin(0);
            object.setInteractive();
            object.setSize(30, 60);
            object.setOffset(50, 30);
            object.setDepth(10);
            object.setDisplaySize(this.generalSize, this.generalSize);
            s.object = object;

            var icon = this.add.image(object.getCenter().x - 10, object.y - 25, "help");
            icon.setOrigin(0);
            icon.setDisplaySize(20, 20);

            s.icon = icon;

            var collegeObject = this.map.getObjectLayer("College Zones").objects.find((ob: TiledGameObject) => {
                return ob.name === s.college.id;
            }) as TiledGameObject;
            s.college.zone = new Phaser.Geom.Rectangle(collegeObject.x, collegeObject.y, collegeObject.width, collegeObject.height);
            this.students.push(s);
            this.colleges.push(s.college);
        });
    }

    updateCurrentStudentNPC() {
        if (this.currentStudent != null) {
            this.currentStudent.object.update();

            //NPC Directional Facing Controls
            if (this.currentStudent.object.x - this.player.x >= 0) {
                this.currentStudent.object.setFlipX(true);
            }
            else if (this.currentStudent.object.x - this.player.x < 0) {
                this.currentStudent.object.setFlipX(false);
            }

            if (this.npcUp) {
                this.currentStudent.object.y = this.player.y + 60;
                this.currentStudent.object.x = this.player.x;
            }
            else if (this.npcDown) {
                this.currentStudent.object.y = this.player.y - 40;
                this.currentStudent.object.x = this.player.x;
            }
            else if (this.npcRight) {
                this.currentStudent.object.y = this.player.y + 12;
                this.currentStudent.object.x = this.player.x - 40;
            }
            else if (this.npcLeft) {
                this.currentStudent.object.y = this.player.y + 12;
                this.currentStudent.object.x = this.player.x + 40;
            }

            // If they took them to the wrong college, show a little feedback to remind the user to look at the map
            if (this.isNPCInWrongCollege()) {
                this.speechBubble.setVisible(true);
            }
            else {
                this.speechBubble.setVisible(false);
            }
        }
    }

    checkKeys(): void {
        // Player is overlapping with a visible key, and they don't already have a key
        if (Phaser.Geom.Intersects.RectangleToRectangle(this.player.getBounds(), this.keyIcons[0].getBounds()) && this.keyIcons[0].visible && !this.playerHasKey) {
            this.keyIcons[0].setVisible(false);
            this.playerHasKey = true;
        }
        else if (Phaser.Geom.Intersects.RectangleToRectangle(this.player.getBounds(), this.keyIcons[1].getBounds()) && this.keyIcons[1].visible && !this.playerHasKey) {
            this.keyIcons[1].setVisible(false);
            this.playerHasKey = true;
        }
    }

    checkGates(): void {
        for (var i = 0; i < this.gateIcons.length; i++) {
            var gate = this.gateIcons[i];
            // If the gate is there and player runs into it:
            if (gate.visible && Phaser.Geom.Intersects.RectangleToRectangle(this.player.getBounds(), gate.getBounds())) {
                // If the player has a key, open the gate and then reset so they don't have a key anymore (they have to get the other one)
                if (this.playerHasKey) {
                    this.gateCollisions[i].active = false;
                    gate.setVisible(false);
                    this.playerHasKey = false;
                }
                else {
                    // Player doesn't have a key, so show the text that they need a key
                    this.gateText.setVisible(true);
                    this.gateTextBorder.setVisible(true);
                }
            }
        }

        // If the text is showing and the player isn't by a gate, hide the text
        if (this.gateText.visible &&
            !Phaser.Geom.Intersects.RectangleToRectangle(this.player.getBounds(), this.gateIcons[0].getBounds()) &&
            !Phaser.Geom.Intersects.RectangleToRectangle(this.player.getBounds(), this.gateIcons[1].getBounds())) {
            // Don't show gate text
            this.gateText.setVisible(false);
            this.gateTextBorder.setVisible(false);
        }
    }

    makeGateText(): void {
        var config = { fontFamily: "Russo One, Roboto Mono, Arial", fill: "#ffffff", align: "center", fontSize: 18 };
        var txt = TranslateApi.translate(this.translateString + "gate");
        this.gateText = this.add.text(this.gameWidth / 2, this.gameHeight / 2, txt, config);
        this.gateText.setPadding(20, 20, 20, 20).setBackgroundColor("#000000").setDepth(25).setScrollFactor(0).setOrigin(.5).setVisible(false);
        this.gateTextBorder = this.add.image(this.gateText.x, this.gateText.y, "pixel").setDepth(24.5).setScrollFactor(0).setOrigin(.5);
        this.gateTextBorder.setDisplaySize(this.gateText.displayWidth + 10, this.gateText.displayHeight + 10).setVisible(false);
    }


    checkIfNPCComplete() {
        if (this.currentStudent != null && Phaser.Geom.Intersects.RectangleToRectangle(this.currentStudent.object.getBounds(), this.currentStudent.college.zone)
            && !this.currentStudent.complete
            // Also force player to be in the college before we say they got them (some edge cases can falsely put npc in college zone)
            && Phaser.Geom.Intersects.RectangleToRectangle(this.player.getBounds(), this.currentStudent.college.zone)) {
            this.modalPop(TranslateApi.translate(this.currentStudent.goodbye), "ok");

            this.sound.stopAll();
            var goodbyeAudio = this.sound.add(this.currentStudent.goodbye);
            goodbyeAudio.play();

            this.currentStudent.complete = true;
            this.putStudentInCollege();
        }
        else if (this.allNPCsComplete() && this.astronaut == null) {
            if (!this.isModalOnScreen()) {
                var displayText = TranslateApi.translate(this.translateString + "endText");
                this.endGame = true;
                this.modalPop(displayText, "done");
            }
        }
    }
    
    /**
     * Checks if the current npc is in the wrong college
     */
    isNPCInWrongCollege(): boolean {
        var college: College;
        for (college of this.colleges) {
            // If the player and npc are in the wrong college, tell them (some edge cases can falsely put npc in college zone)
            if (college.id !== this.currentStudent.college.id && Phaser.Geom.Intersects.RectangleToRectangle(this.currentStudent.object.getBounds(), college.zone)
                && Phaser.Geom.Intersects.RectangleToRectangle(this.player.getBounds(), college.zone)) {
                return true;
            }
        }

        return false;
    }

    /**
     * Put candidate in center of booth and disable them from leaving
     */
    putStudentInCollege(): void {
        var college = this.currentStudent.college.zone;
        var right = college.x + 3 * college.width / 4;
        var bottom = college.y + 3 * college.height / 4;
        this.add.tween({
            targets: this.currentStudent.object,
            x: right,
            y: bottom,
            ease: Phaser.Math.Easing.Circular,
            duration: 1000,
            onStart: () => {
                this.currentStudent.object.disableBody();
            },
            onComplete: () => {
                this.currentStudent = null;
            }
        });
    }

    allNPCsComplete(): boolean {
        var student: Student;
        for (student of this.students) {
            if (!student.complete) {
                return false;
            }
        }
        return true;
    }

    /**
    * Checks if there is already a modal open on screen (we don't want to be able to add more than one modal to a scene)
    */
    isModalOnScreen(): boolean {
        var modal = this.getModal();
        return modal !== null;
    }

    getModal(): PhaserModal {
        for (var c of this.children.list) {
            if (c.type === "PhaserModal") {
                return c as PhaserModal;
            }
        }

        return null;
    }

    update(): void {
        if (!this.endGame) {
            this.updatePlayerMovement();
            this.updateCurrentStudentNPC();
            this.checkIfNPCComplete();
            this.checkKeys();
            this.checkGates();
        }
    }

    /**
     * GamePad/Player Movement
     */
    generateGamepad(): void {
        this.gamePad = new PhaserGamePad(this);
        this.gamePad.on("keydown_a", this.aDown);
        this.gamePad.on("keydown_b", this.bDown);
        this.gamePad.on("keyup_a", this.stopA);
    }

    /**
    * Talk to character
    */
    bDown() {
        var modal = this.getModal();
        var isModalOnScreen = modal !== null;
        var playerBounds = this.player.getBounds();

        if (isModalOnScreen) {
            this.modalOptionClicked(modal);
            return;
        }

        // Only need to do this if we don't already have a student
        if (this.currentStudent == null) {
            var student: Student;
            for (student of this.students) {
                var studentArea = student.object.getBounds();
                studentArea.width += 25;
                studentArea.height += 25;
                studentArea.centerX = student.object.getCenter().x;
                studentArea.centerY = student.object.getCenter().y;
                // If we are next to student, they are not already done, and there isn't already a modal on the screen, start talking to this character
                if (Phaser.Geom.Intersects.RectangleToRectangle(playerBounds, studentArea) && !student.complete && !isModalOnScreen) {
                    this.currentStudent = student;
                    this.currentStudent.icon.destroy();
                    this.interactWithCurrentStudent();
                    break;
                }
            }
        }
        else {
            this.interactWithCurrentStudent();
        }        
    }

    interactWithCurrentStudent() {
        var student = this.currentStudent;
        this.modalPop(TranslateApi.translate(student.greeting), "yes");
        this.sound.stopAll();
        var greetingAudio = this.sound.add(student.greeting);
        greetingAudio.play();
    }

    /**
     * Show pop up map
     */
    aDown() {
        this.popupMap.setVisible(true);
        this.popupText[0].setVisible(true);
        this.popupText[1].setVisible(true);
        this.popupText[2].setVisible(true);
        this.popupText[3].setVisible(true);
        this.gamePad.setDepth(-1);
        this.stopAllMovement = true;
    }

    stopA(): void {
        this.popupMap.setVisible(false);
        this.popupText[0].setVisible(false);
        this.popupText[1].setVisible(false);
        this.popupText[2].setVisible(false);
        this.popupText[3].setVisible(false);
        this.cameras.main.setVisible(true);
        this.gamePad.setDepth(1);
        this.stopAllMovement = false;
    }

    updatePlayerMovement(): void {
        if (this.popupMap.visible || this.isModalOnScreen()) {
            this.player.anims.play('turn');
            return;
        }

        this.xVel = 0;
        this.yVel = 0;

        if (this.gamePad.keys.left.isDown) {
            this.xVel += -this.speed;
            this.npcLeft = true;
            this.npcRight = false;
            this.npcUp = false;
            this.npcDown = false;
        }
        if (this.gamePad.keys.right.isDown) {
            this.xVel += this.speed;
            this.npcLeft = false;
            this.npcRight = true;
            this.npcUp = false;
            this.npcDown = false;
        }
        if (this.gamePad.keys.up.isDown) {
            this.yVel += -this.speed;
            this.npcLeft = false;
            this.npcRight = false;
            this.npcDown = false;
            this.npcUp = true;
        }
        if (this.gamePad.keys.down.isDown) {
            this.yVel += this.speed;
            this.npcLeft = false;
            this.npcRight = false;
            this.npcUp = false;
            this.npcDown = true;
        }

        this.player.setVelocity(this.xVel, this.yVel);

        // Play walking animations
        if (this.xVel > 0) {
            this.player.anims.play('right', true);
        }
        else if (this.xVel < 0) {
            this.player.anims.play('left', true);
        }
        else {
            this.player.anims.play('turn');
        }
    }

}



// WEBPACK FOOTER //
// ./src/games/UniversityGame/scenes/MainScene.ts